import { getDataAPI, postDataAPI, putDataAPI } from './fetchData';
import axios from 'axios';
import { BASE_URL } from './axios';

// Debug log to see what BASE_URL is being used
console.log('BASE_URL in promptsApi:', BASE_URL);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Cache configuration for custom prompts
const CUSTOM_PROMPTS_CACHE_KEY = 'dizee_custom_prompts_cache';
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

// Memory cache for current session
let customPromptsMemoryCache = {
  data: null,
  timestamp: null,
  userId: null
};

// Helper function to get current user ID from token
const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('dizeeToken');
    if (!token) return null;
    
    // Parse JWT token to get user ID (basic parsing)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id || payload.userId || payload.user_id;
  } catch (error) {
    return null;
  }
};

// Helper function to check if cache is valid
const isCacheValid = (cacheData, userId) => {
  if (!cacheData || !cacheData.timestamp || !cacheData.data) {
    return false;
  }
  
  // Check if cache is for the same user
  if (cacheData.userId !== userId) {
    return false;
  }
  
  // Check if cache is not expired
  const now = Date.now();
  return (now - cacheData.timestamp) < CACHE_DURATION;
};

// Helper function to save to cache
const saveToCache = (data, userId) => {
  const cacheData = {
    data,
    timestamp: Date.now(),
    userId
  };
  
  // Save to memory cache
  customPromptsMemoryCache = cacheData;
  
  // Also save to global window object for synchronous access
  window.dizeeCustomPromptsCache = cacheData;
  
  // Save to localStorage cache
  try {
    localStorage.setItem(CUSTOM_PROMPTS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to save custom prompts to localStorage cache:', error);
  }
};

// Helper function to load from cache
const loadFromCache = (userId) => {
  // First check memory cache
  if (isCacheValid(customPromptsMemoryCache, userId)) {
    console.log('📋 Loading custom prompts from memory cache');
    return customPromptsMemoryCache.data;
  }
  
  // Then check localStorage cache
  try {
    const cached = localStorage.getItem(CUSTOM_PROMPTS_CACHE_KEY);
    if (cached) {
      const cacheData = JSON.parse(cached);
      if (isCacheValid(cacheData, userId)) {
        console.log('📋 Loading custom prompts from localStorage cache');
        // Update memory cache
        customPromptsMemoryCache = cacheData;
        return cacheData.data;
      }
    }
  } catch (error) {
    console.warn('Failed to load custom prompts from localStorage cache:', error);
  }
  
  return null;
};

export const promptsApi = {
  // Get all user prompts (with caching)
  getUserPrompts: async (forceRefresh = false) => {
    try {
      const userId = getCurrentUserId();
      
      // Try to load from cache first (unless force refresh is requested)
      if (!forceRefresh && userId) {
        const cachedData = loadFromCache(userId);
        if (cachedData) {
          return cachedData;
        }
      }
      
      const token = localStorage.getItem('dizeeToken');
      console.log('📥 Fetching custom prompts from database...');
      const response = await getDataAPI('user/prompts', token);
      
      console.log('Prompts response:', response.data);
      console.log('Prompts array:', response.data?.data?.prompts);
      console.log('Number of prompts:', response.data?.data?.prompts?.length || 0);
      
      const prompts = response.data?.data?.prompts || [];
      
      // Save to cache
      if (userId) {
        saveToCache(prompts, userId);
        console.log('💾 Saved custom prompts to cache');
      }
      
      return prompts;
    } catch (error) {
      console.error('❌ Error fetching prompts:', error);
      throw error;
    }
  },

  // Clear cache (useful when user logs out or prompts are updated)
  clearCache: () => {
    customPromptsMemoryCache = { data: null, timestamp: null, userId: null };
    try {
      localStorage.removeItem(CUSTOM_PROMPTS_CACHE_KEY);
      console.log('🗑️ Cleared custom prompts cache');
    } catch (error) {
      console.warn('Failed to clear custom prompts cache:', error);
    }
  },

  // Create a new prompt
  createPrompt: async (promptData) => {
    try {
      console.log('Creating prompt with data:', promptData);
      const token = localStorage.getItem('dizeeToken');
      
      const response = await postDataAPI('user/prompts', promptData, token);
      
      console.log('Full response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Created prompt:', response.data?.data?.prompt);
      
      if (response.status === 201 || response.status === 200) {
        console.log('✅ Prompt created successfully!');
        // Clear cache since prompts have been updated
        promptsApi.clearCache();
        return response.data?.data?.prompt;
      } else {
        console.warn('⚠️ Unexpected response status:', response.status);
        // Clear cache since prompts have been updated
        promptsApi.clearCache();
        return response.data?.data?.prompt;
      }
    } catch (error) {
      console.error('❌ Error creating prompt:', error);
      throw error;
    }
  },

  // Get a specific prompt by ID
  getPromptById: async (id) => {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await getDataAPI(`user/prompts/${id}`, token);
      
      return response.data?.data?.prompt;
    } catch (error) {
      console.error('Error fetching prompt:', error);
      throw error;
    }
  },

  // Update a prompt
  updatePrompt: async (id, promptData) => {
    try {
      console.log('🔄 Frontend updating prompt:', id, 'with data:', promptData);
      const token = localStorage.getItem('dizeeToken');
      const response = await putDataAPI(`user/prompts/${id}`, promptData, token);
      
      console.log('✅ Frontend update response:', response.data);
      // Clear cache since prompts have been updated
      promptsApi.clearCache();
      return response.data?.data?.prompt;
    } catch (error) {
      console.error('❌ Error updating prompt:', error);
      throw error;
    }
  },

  // Delete a prompt
  deletePrompt: async (id) => {
    try {
      const token = localStorage.getItem('dizeeToken');
      await axios.delete(`${BASE_URL}/user/prompts/${id}`, {
        headers: { 'Authorization': token }
      });
      
      // Clear cache since prompts have been updated
      promptsApi.clearCache();
      return true;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  },

  // Synchronously load cached custom prompts (for immediate UI rendering)
  getCachedCustomPrompts: () => {
    const userId = getCurrentUserId();
    if (userId) {
      return loadFromCache(userId) || [];
    }
    return [];
  }
}; 